import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, SITE } from "@/config/site";
import { trackEvent } from "@/lib/analytics";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-300 ${
        scrolled || open
          ? "bg-white/85 backdrop-blur-xl border-b border-ink/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 sm:px-8 h-20">
        <Link
          to="/"
          data-testid="nav-logo-link"
          aria-label="Nutrimove Lab — Home"
          className="flex items-center gap-3"
        >
          <img
            src={SITE.images.logo}
            alt="Logo Nutrimove Lab — Dr. Michele Ceccarelli"
            className="h-12 w-12 object-contain"
            width={48}
            height={48}
          />
          <span className="hidden sm:block leading-tight">
            <span className="block font-display text-lg tracking-tight">Nutrimove Lab</span>
            <span className="block text-[10px] uppercase tracking-[0.22em] text-ink/50">
              Nutrizione · Movimento
            </span>
          </span>
        </Link>

        <nav aria-label="Navigazione principale" className="hidden lg:flex items-center gap-5 xl:gap-6">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={`nav-link-${link.label.toLowerCase().replace(" ", "-")}`}
              className={({ isActive }) =>
                `relative text-sm font-medium tracking-wide transition-colors duration-200 hover:text-ink ${
                  isActive ? "text-ink after:absolute after:-bottom-1.5 after:left-0 after:h-[2px] after:w-full after:bg-leaf" : "text-ink/55"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={SITE.bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-booking-cta"
            onClick={() => trackEvent("booking_click", { cta: "navbar" })}
            className="hidden sm:inline-flex items-center rounded-full bg-ink px-6 py-2.5 text-xs font-bold uppercase tracking-[0.16em] text-white transition-colors duration-300 hover:bg-leaf-deep"
          >
            Prenota una visita
          </a>
          <button
            type="button"
            data-testid="nav-mobile-menu-button"
            aria-label={open ? "Chiudi il menu" : "Apri il menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-full border border-ink/15 text-ink"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            aria-label="Menu mobile"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-b border-ink/10"
          >
            <div className="px-6 pb-8 pt-2 flex flex-col">
              {NAV_LINKS.map((link, i) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, x: -14 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.4 }}
                >
                  <NavLink
                    to={link.to}
                    data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(" ", "-")}`}
                    className={({ isActive }) =>
                      `block border-b border-ink/8 py-4 font-display text-2xl tracking-tight ${
                        isActive ? "text-leaf-deep" : "text-ink"
                      }`
                    }
                  >
                    {link.label}
                  </NavLink>
                </motion.div>
              ))}
              <a
                href={SITE.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-nav-booking-cta"
                onClick={() => trackEvent("booking_click", { cta: "navbar-mobile" })}
                className="mt-6 inline-flex items-center justify-center rounded-full bg-ink px-6 py-4 text-sm font-bold uppercase tracking-[0.16em] text-white"
              >
                Prenota una visita
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
